import { useCallback, useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { trackShare } from "@/lib/analytics";
import { useFavorites } from "@/lib/favorites";
import { cn } from "@/lib/utils";
import type { Ad } from "@/lib/data";

const WATCH_KEY = "albazar:price-watch";

type Props = {
  ad: Ad;
  className?: string;
};

function readWatchIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(WATCH_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeWatchIds(ids: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(WATCH_KEY, JSON.stringify(ids));
  } catch {
    /* kvota dolu ola bilər */
  }
}

function usePriceWatch(id: string) {
  const [watching, setWatching] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setWatching(readWatchIds().includes(id));
    setReady(true);
  }, [id]);

  const toggle = useCallback(() => {
    const list = readWatchIds();
    const willWatch = !list.includes(id);
    const next = willWatch ? [id, ...list] : list.filter((x) => x !== id);
    writeWatchIds(next);
    setWatching(willWatch);
    return willWatch;
  }, [id]);

  return { watching: ready && watching, toggle, ready };
}

/** Məhsul səhifəsindəki əməliyyat düymələri: Seçilmişlər (ürək ikonası) +
 *  yazı ilə WhatsApp, Kopyala, Paylaş, Qiyməti izlə. */
export function ShareBar({ ad, className }: Props) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `${ad.title}${ad.price ? ` — ${ad.price} AZN` : ""} | Albazar.az`;
  const { isFavorite, toggle: toggleFavorite } = useFavorites();
  const { watching, toggle: toggleWatch, ready: watchReady } = usePriceWatch(String(ad.id));
  const liked = isFavorite(ad.id);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      void trackShare(String(ad.id), "kopyalama");
      toast.success("Link kopyalandı");
    } catch {
      toast.error("Link kopyalanmadı");
    }
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: ad.title, text, url });
        void trackShare(String(ad.id), "sistem");
      } else {
        await copy();
      }
    } catch {
      /* istifadəçi ləğv etdi */
    }
  };

  const openWhatsApp = () => {
    const href = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;
    void trackShare(String(ad.id), "whatsapp");
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const handleFavorite = () => {
    const added = toggleFavorite(ad);
    toast.success(added ? "Seçilmişlərə əlavə olundu" : "Seçilmişlərdən çıxarıldı");
  };

  const handleWatch = () => {
    if (!watchReady) return;
    const added = toggleWatch();
    toast.success(added ? "Qiymət izləməyə əlavə olundu" : "Qiymət izləməsindən çıxarıldı");
  };

  const textBtn =
    "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-border bg-card px-3 text-[11px] font-semibold text-foreground transition-all active:scale-95 hover:bg-accent sm:px-3.5 sm:text-xs whitespace-nowrap";

  return (
    <div
      className={cn(
        "flex flex-nowrap items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <button
        type="button"
        onClick={handleFavorite}
        title={liked ? "Seçilmişlərdən çıxar" : "Seçilmişlərə əlavə et"}
        aria-label={liked ? "Seçilmişlərdən çıxar" : "Seçilmişlərə əlavə et"}
        aria-pressed={liked}
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-full border border-border transition-all active:scale-95",
          liked ? "bg-destructive/10 text-destructive" : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground",
        )}
      >
        <Heart className={cn("size-4", liked && "fill-destructive")} strokeWidth={2.2} />
      </button>

      <button
        type="button"
        onClick={openWhatsApp}
        className={cn(textBtn, "hover:border-emerald-500/40 hover:text-emerald-600")}
      >
        WhatsApp
      </button>
      <button type="button" onClick={copy} className={textBtn}>
        Kopyala
      </button>
      <button type="button" onClick={share} className={textBtn}>
        Paylaş
      </button>
      <button
        type="button"
        onClick={handleWatch}
        className={cn(
          textBtn,
          watching && "border-info/50 bg-info/10 text-info hover:bg-info/20",
        )}
      >
        {watching ? "İzlənir" : "Qiyməti izlə"}
      </button>
    </div>
  );
}
